






























































import { Component, Vue, Prop } from "vue-property-decorator";
import { CPTCreateModel } from "@/models/cpt/CPTCreateModel";
import CustomPostTypeService from "@/api/CustomPostTypeService";
import { Subscription, interval } from "rxjs";
import { ACTION_FETCH_POW, ACTION_LOGIN_ANON } from "@/store/actions-types";
import {
  GET_ANON_AUTH_CPT_CONTEXT,
  GET_GITLIFY_CONFIG,
  GET_POW_CHALLENGE_SOLUTION,
} from "@/store/getters-types";
import { RESET_AUTH_STATE, RESET_CPT_STATE } from "@/store/mutations-types";
import { GitlifyConfig } from "@/models/common/GitlifyConfig";
import PasswordFormGroup from "@/components/user-management/PasswordFormGroup.vue";
import { PasswordFormGroupModelI } from "@/models/admin/user-management/PasswordFormGroupModelI";

@Component({
  components: {
    PasswordFormGroup,
  },
})
/**
 * View where users solve the pow and then create a cpt and receive an jwt
 */
export default class CreateCPT extends Vue {
  @Prop({ required: true, type: String }) readonly cpt!: string;

  CPTCreateObj: CPTCreateModel = new CPTCreateModel();
  service = new CustomPostTypeService();

  /**
   * The seconds to wait before a POW solution can be submitted
   */
  sendSolutionCountDown = 0;

  private countDownSubscription: Subscription | undefined;

  /**
   * check if working on the pow
   * @return boolean
   */
  get isSolvingProofOfWork(): boolean {
    return !this.$store.getters[GET_POW_CHALLENGE_SOLUTION];
  }

  /**
   * check if form is valid and pow done
   * @return boolean
   */
  get isSubmittable(): boolean {
    return (
      !this.isSolvingProofOfWork &&
      this.sendSolutionCountDown === 0 &&
      this.formValid
    );
  }

  /**
   * get the cpt id after doing the proof of work
   */
  get cptId(): string | undefined {
    const anonAuthContext = this.$store.getters[GET_ANON_AUTH_CPT_CONTEXT];
    return anonAuthContext?.cptId;
  }

  /**
   * the password and it's validation state
   */
  public passwordFormState: PasswordFormGroupModelI = {
    password: "",
    valid: null,
  };

  /**
   * validate the form fields
   */
  get formValid(): boolean {
    return !!this.passwordFormState.valid;
  }

  /**
   * make sure the auth/cpt state is clean
   * before starting all over
   */
  beforeMount(): void {
    this.$store.commit(RESET_AUTH_STATE);
    this.$store.commit(RESET_CPT_STATE);
  }

  /**
   * start to solve the pow on view enter
   * @return void
   */
  async mounted(): Promise<void> {
    this.CPTCreateObj.cpt = this.cpt;
    await this.$store.dispatch(ACTION_FETCH_POW);
    this.countDownTimer();
  }

  /**
   * get a token submitting the challenge solution, your password and the post type
   * @return void
   */
  async getAuthJWT(): Promise<void> {
    this.CPTCreateObj.password = this.passwordFormState.password;
    await this.$store.dispatch(ACTION_LOGIN_ANON, this.CPTCreateObj);
  }

  /**
   * Forward the user to the edit form
   * after successful authentication
   */
  async forwardToCptEdit(): Promise<void> {
    await this.getAuthJWT();
    if (this.cptId) {
      this.$router.push({
        name: "editCPT",
        params: { cpt: this.cpt, id: this.cptId },
      });
    }
  }

  /**
   * count down until we can send the POW solution to the api
   */
  countDownTimer(): void {
    const config: GitlifyConfig = this.$store.getters[GET_GITLIFY_CONFIG];
    this.sendSolutionCountDown = config.pow.timeout / 1000;
    this.countDownSubscription = interval(1000).subscribe(() => {
      if (this.sendSolutionCountDown > 0) {
        this.sendSolutionCountDown -= 1;
      } else {
        this.countDownSubscription?.unsubscribe();
      }
    });
  }

  /**
   * unsubscribe to prevent memory leaks
   */
  ngOnDestroy(): void {
    this.countDownSubscription?.unsubscribe();
  }
}
